import { useMemo, useState } from "react";
import { CalendarCheck2, CalendarClock, CircleSlash, House, Plane, Trophy, Users } from "lucide-react";

import { usePageBack } from "../../context/PageBackContext.js";
import { cn } from "../../lib/utils.js";
import { SummaryStatSuffix } from "../../components/SummaryStatCard.js";
import { DropdownMultiSelect, DropdownSelect } from "../../components/DropdownButton.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { Badge, type BadgeProps } from "../../components/ui/badge.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import type { PaginationMeta } from "../../components/ui/pagination.js";
import {
  ReportPage, FilterBar, ErrorBanner, OrgFilters, ExportButton, CardGrid, ReportStatCard, ReportTableFrame, ReportFooter, PageSizeSelect, TopListCard,
  STATUS_GROUP_OPTIONS, TableScrollStart, TableToolbar, dropdownProps, splitList, joinList, stickyName, stickyCode, NAME_WIDTH, CODE_WIDTH, type OpenState,
} from "../../components/reports/ReportKit.js";
import {
  useUrlFilters, useDebouncedSearch, useReport, useOrgMeta, toParams, downloadReport,
} from "../../components/reports/useReport.js";

interface LeaveFigures { entitled: number; taken: number; pending: number; balance: number }
interface LeaveTypeColumn { code: string; label: string; trackBalance: boolean; probationBlocked: boolean; enabled: boolean; quarterly: boolean }
type WfhState = "used" | "over" | "pending" | "available" | "unused" | "upcoming";
interface WfhQuarterCell { days: number; state: WfhState; t: string }
interface LeaveRow {
  id: string; name: string; code: string; department: string | null; office: string | null; status: string;
  onProbation: boolean; probationEnd: string | null;
  types: Record<string, LeaveFigures | null>;
  wfhQuarters: WfhQuarterCell[];
  approvedDays: number; pendingDays: number; lopDays: number; lowBalance: boolean;
}
interface LeaveReport {
  year: number;
  allTypes: LeaveTypeColumn[];
  columns: LeaveTypeColumn[];
  rows: LeaveRow[];
  /** Loaded by a separate request (`part=cards`), so a page turn never refetches it. */
  cards?: {
    employees: number; totalTaken: number; pendingRequests: number; pendingDays: number;
    onLeaveToday: number; lowBalance: number; lopDays: number; wfhThisQuarter: number;
    topTakers: { id: string; name: string; code: string; days: number }[];
  };
  meta: PaginationMeta;
}

const THIS_YEAR = String(new Date().getFullYear());
const DEFAULTS = {
  search: "", officeIds: "", departmentIds: "", statusGroup: "current", year: THIS_YEAR,
  leaveTypes: "", page: "1", limit: "25",
};
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const y = String(Number(THIS_YEAR) + 1 - i);
  return { value: y, label: y };
});
const SUB = ["Entitled", "Taken", "Pending", "Balance"] as const;
/** WFH is one week a quarter, so it gets a column per quarter instead of a yearly balance. */
const QUARTERS = [
  { key: "Q1", months: "Jan–Mar" }, { key: "Q2", months: "Apr–Jun" },
  { key: "Q3", months: "Jul–Sep" }, { key: "Q4", months: "Oct–Dec" },
] as const;
// The cards total every leave type; the type pick only chooses table columns.
const ROW_ONLY = ["leaveTypes"];

const API = "/reports/hr/leave";

const WFH_BADGE: Record<WfhState, { tone: BadgeProps["tone"]; text: (d: number) => string }> = {
  used: { tone: "info", text: (d) => `${d} d` },
  over: { tone: "warning", text: (d) => `${d} d +EL` },
  pending: { tone: "warning", text: (d) => `${d} d pending` },
  available: { tone: "success", text: () => "Available" },
  unused: { tone: "neutral", text: () => "Not used" },
  upcoming: { tone: "outline", text: () => "—" },
};

/** Balance cell colour: red when nothing is left, amber when low. */
function balanceClass(b: number): string {
  if (b <= 0) return "bg-[var(--color-error-bg)] text-[var(--color-error-fg)]";
  if (b <= 1) return "bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]";
  return "bg-[var(--color-success-bg)] text-[var(--color-success-fg)]";
}

export default function LeaveReportPage() {
  usePageBack("/dashboard");
  const [f, setF] = useUrlFilters(DEFAULTS);
  const [search, setSearch] = useDebouncedSearch(f.search, (v) => setF({ search: v }));
  const [open, setOpen] = useState<string | null>(null);
  const openState: OpenState = { open, setOpen };
  const org = useOrgMeta();

  const params = useMemo(() => toParams(f), [f]);
  const { data, cards: c, loading, error } = useReport<LeaveReport>(API, params, "LeaveReportPage", ROW_ONLY);

  // Options are every enabled type; the ticks are the columns actually shown, so on
  // first load Casual Leave is listed but unticked (the API's default set).
  const typeOptions = useMemo(() => (data?.allTypes ?? []).map((t) => ({ value: t.code, label: t.label })), [data?.allTypes]);
  const columns = data?.columns ?? [];
  const shownTypes = f.leaveTypes ? splitList(f.leaveTypes) : columns.map((col) => col.code);
  const rows = data?.rows ?? [];

  return (
    <ReportPage
      title="Leave Reports"
      description="Entitlement, leave taken, pending and balance for every leave type, per employee."
    >
      <div className="flex flex-col gap-3">
        <CardGrid>
          <ReportStatCard icon={Users} tone="brand" label="Employees" value={c?.employees ?? "—"} />
          <ReportStatCard icon={CalendarCheck2} tone="info" label="Leave Taken" value={<>{c?.totalTaken ?? "—"}<SummaryStatSuffix> days</SummaryStatSuffix></>} caption={`Approved in ${f.year}`} />
          <ReportStatCard icon={CalendarClock} tone="warning" label="Pending" value={c?.pendingRequests ?? "—"} caption={c ? `${c.pendingDays} days awaiting approval` : undefined} />
          <ReportStatCard icon={Plane} tone="cyan" label="On Leave Today" value={c?.onLeaveToday ?? "—"} />
          <ReportStatCard icon={CircleSlash} tone="danger" label="LOP Days" value={c?.lopDays ?? "—"} caption="Loss of pay" />
          <ReportStatCard icon={House} tone="accent" label="WFH This Quarter" value={c?.wfhThisQuarter ?? "—"} caption="Used their 5-day week" />
        </CardGrid>
        <TopListCard
          icon={Trophy}
          title={`Top 5 leave takers · ${f.year}`}
          empty="No approved leave yet."
          items={(c?.topTakers ?? []).map((t) => ({ key: t.id, primary: t.name, secondary: t.code, value: `${t.days} d` }))}
        />
      </div>

      <FilterBar>
        <SearchInput className="w-60" placeholder="Search name, code or email" value={search} onChange={(e) => setSearch(e.target.value)} />
        <OrgFilters meta={org} officeIds={f.officeIds} departmentIds={f.departmentIds} onChange={setF} openState={openState} />
        <DropdownSelect
          value={f.statusGroup} onChange={(v) => setF({ statusGroup: v })} options={STATUS_GROUP_OPTIONS}
          placeholder="Current employees" ariaLabel="Employees" clearable={false} {...dropdownProps("status", openState)}
        />
        <DropdownSelect
          value={f.year} onChange={(v) => setF({ year: v })} options={YEAR_OPTIONS}
          placeholder="Year" ariaLabel="Year" clearable={false} {...dropdownProps("year", openState)}
        />
        <DropdownMultiSelect
          values={shownTypes} onChange={(v) => setF({ leaveTypes: joinList(v) })} options={typeOptions}
          allLabel="All leave types" ariaLabel="Leave type" {...dropdownProps("type", openState)}
        />
      </FilterBar>

      <TableToolbar>
        <PageSizeSelect limit={f.limit} onLimit={(v) => setF({ limit: v })} openState={openState} />
        <ExportButton onExport={() => downloadReport(API, params, `leave-report-${f.year}.xlsx`)} />
      </TableToolbar>

      <ErrorBanner message={error} />

      <ReportTableFrame loading={loading} empty={rows.length === 0} emptyText="No employees match these filters." contentKey={columns.map((col) => col.code).join(",")}>
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow className="bg-surface-50">
              <TableHead rowSpan={2} className={cn(stickyName, "bg-surface-50 align-bottom")}><div className={NAME_WIDTH}><TableScrollStart />Employee Name</div></TableHead>
              <TableHead rowSpan={2} className={cn(stickyCode, "bg-surface-50 align-bottom")}><div className={CODE_WIDTH}>Emp Code</div></TableHead>
              {columns.map((col) => (
                <TableHead
                  key={col.code} colSpan={4} className="border-l border-l-surface-300 text-center normal-case text-surface-700"
                  title={col.quarterly ? "One week (5 days) of WFH per quarter; extra days are charged to Earned Leave" : undefined}
                >
                  {col.label}
                  {col.quarterly && <span className="ml-1 text-[10px] font-normal text-surface-500">· 5 days / quarter</span>}
                </TableHead>
              ))}
              <TableHead colSpan={3} className="border-l border-l-surface-300 text-center">Totals</TableHead>
            </TableRow>
            <TableRow className="bg-surface-50">
              {columns.flatMap((col) => (col.quarterly
                ? QUARTERS.map((q, i) => (
                    <TableHead key={`${col.code}-${q.key}`} title={q.months} className={cn("px-2 text-center text-[10px]", i === 0 && "border-l border-l-surface-300")}>{q.key}</TableHead>
                  ))
                : SUB.map((s, i) => (
                    <TableHead key={`${col.code}-${s}`} className={cn("px-2 text-center text-[10px]", i === 0 && "border-l border-l-surface-300")}>{s}</TableHead>
                  ))))}
              <TableHead className="border-l border-l-surface-300 px-2 text-center text-[10px]">Approved</TableHead>
              <TableHead className="px-2 text-center text-[10px]">Pending</TableHead>
              <TableHead className="px-2 text-center text-[10px]">LOP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="group hover:bg-surface-50">
                <TableCell className={cn(stickyName, "bg-surface-0 group-hover:bg-surface-50")}>
                  <div className={cn(NAME_WIDTH, "flex items-center gap-1.5")}>
                    <span className="truncate font-semibold text-surface-900" title={[r.name, r.department, r.office].filter(Boolean).join(" · ")}>{r.name}</span>
                    {r.onProbation && <Badge tone="warning" title={`On probation until ${r.probationEnd ?? "—"}`}>Probation</Badge>}
                  </div>
                </TableCell>
                <TableCell className={cn(stickyCode, "bg-surface-0 text-surface-600 group-hover:bg-surface-50")}>
                  <div className={cn(CODE_WIDTH, "truncate")}>{r.code}</div>
                </TableCell>
                {columns.flatMap((col) => {
                  if (col.quarterly) {
                    return r.wfhQuarters.map((w, i) => (
                      <TableCell key={`${col.code}-q${i}`} className={cn("px-2 text-center", i === 0 && "border-l border-l-surface-300")}>
                        <Badge tone={WFH_BADGE[w.state].tone} title={w.t}>{WFH_BADGE[w.state].text(w.days)}</Badge>
                      </TableCell>
                    ));
                  }
                  const fig = r.types[col.code];
                  const locked = col.probationBlocked && r.onProbation;
                  const blank = <span className="text-surface-300">—</span>;
                  const cls = "px-2 text-center tabular-nums";
                  if (!fig) return SUB.map((s, i) => <TableCell key={`${col.code}-${s}`} className={cn(cls, i === 0 && "border-l border-l-surface-300")}>{blank}</TableCell>);
                  return [
                    <TableCell key={`${col.code}-e`} className={cn(cls, "border-l border-l-surface-300")}>{col.trackBalance ? fig.entitled : blank}</TableCell>,
                    <TableCell key={`${col.code}-t`} className={cn(cls, "font-semibold")}>{fig.taken}</TableCell>,
                    <TableCell key={`${col.code}-p`} className={cn(cls, fig.pending > 0 && "text-[var(--color-warning-fg)]")}>{fig.pending}</TableCell>,
                    <TableCell key={`${col.code}-b`} className={cls}>
                      {!col.trackBalance ? blank : locked ? (
                        <Badge tone="warning" title="Locked while on probation">Locked</Badge>
                      ) : (
                        <span className={cn("inline-block min-w-8 rounded-[4px] px-1 py-0.5 text-[12px] font-semibold", balanceClass(fig.balance))}>{fig.balance}</span>
                      )}
                    </TableCell>,
                  ];
                })}
                <TableCell className="border-l border-l-surface-300 px-2 text-center font-semibold tabular-nums">{r.approvedDays}</TableCell>
                <TableCell className="px-2 text-center tabular-nums">{r.pendingDays}</TableCell>
                <TableCell className={cn("px-2 text-center tabular-nums", r.lopDays > 0 && "font-semibold text-[var(--color-error-fg)]")}>{r.lopDays}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ReportTableFrame>

      <ReportFooter
        meta={data?.meta} onPage={(p) => setF({ page: String(p) })}
      />
    </ReportPage>
  );
}
